/**
 * Anatomy — Dashboard. The warm snapshot (`GET api/anatomy/dashboard`) gives state and totals; the
 * two history lists and the meetings list give the series behind the charts. Route: /app/anatomy
 */
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { IconRefresh, IconSend, IconCalendarEvent, IconLink, IconAlertTriangle, IconExternalLink, IconUserPlus, IconListDetails, IconTestPipe, IconSettings, IconPlugConnected } from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import { Stack, Card, Group, Button, Text, Badge, Alert, Grid, SimpleGrid, Skeleton } from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { KpiCardWithSparkline, AreaChartWidget, DonutChartWidget, BarChartWidget, CircularGauge } from '@/components/advanced/Widgets';
import type { TrendData } from '@/components/advanced/Widgets';
import { useAnatomyService } from '@actschurch/ccms-sdk';
import type { AnatomyActivityItem } from '@actschurch/ccms-sdk';
import { usePermissions } from '@/components/ccms-components/auth/hooks/usePermissions';
import { anatomyKeys } from '../utils/queryKeys';
import { formatRelative, stampToDate } from '../utils/format';
import { resultOf } from '../utils/options';
import { PageHeader } from '../components/PageHeader';
import { ActivityTable } from '../components/ActivityTable';

const DAYS = 30;
const CHART_H = 280;

/** Same frame as a chart widget, for when there is nothing to chart yet. */
function ChartPlaceholder({ title, subtitle, text }: { title: string; subtitle: string; text: string }) {
  return (
    <Card withBorder p="lg" h="100%">
      <Stack gap="md" h="100%">
        <div><Text size="lg" fw={600}>{title}</Text><Text size="sm" c="dimmed">{subtitle}</Text></div>
        <Group justify="center" align="center" style={{ flex: 1, minHeight: CHART_H }}><Text size="sm" c="dimmed" ta="center">{text}</Text></Group>
      </Stack>
    </Card>
  );
}
const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayLabel = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

/** One row per day for the last N days, oldest first — zero-filled so charts have a continuous axis. */
function dailySeries(followUps: AnatomyActivityItem[], registrations: AnatomyActivityItem[]) {
  const days: { key: string; day: string; followUps: number; registrations: number; queued: number; failed: number }[] = [];
  const index = new Map<string, number>();
  for (let i = DAYS - 1; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); index.set(dayKey(d), days.length); days.push({ key: dayKey(d), day: dayLabel(d), followUps: 0, registrations: 0, queued: 0, failed: 0 }); }
  const bump = (items: AnatomyActivityItem[], field: 'followUps' | 'registrations') => {
    for (const it of items) {
      const d = stampToDate(it.logDateTime); if (!d) continue;
      const i = index.get(dayKey(d)); if (i === undefined) continue;
      if (it.success && !it.queued) days[i][field] += 1;
      if (it.queued) days[i].queued += 1;
      if (!it.success) days[i].failed += 1;
    }
  };
  bump(followUps, 'followUps'); bump(registrations, 'registrations');
  return days;
}

const trendOf = (current: number, previous: number, label: string): TrendData => {
  if (previous === 0 && current === 0) return { value: 0, direction: 'neutral', label };
  if (previous === 0) return { value: 100, direction: 'up', isPositive: true, label };
  const pct = Math.round(((current - previous) / previous) * 100);
  return { value: Math.abs(pct), direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral', isPositive: pct >= 0, label };
};

export default function AnatomyDashboardPage() {
  const service = useAnatomyService();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasPermission, hasRole } = usePermissions();
  const canSettings = hasRole('Admin') || hasPermission('Anatomy.Settings');

  const snapshot = useQuery({ queryKey: anatomyKeys.dashboard(), queryFn: () => service.getDashboard(), staleTime: 60_000 });
  const followUps = useQuery({ queryKey: anatomyKeys.followUpHistory(500), queryFn: () => service.getFollowUpHistory(500), staleTime: 60_000 });
  const registrations = useQuery({ queryKey: anatomyKeys.registrationHistory(500), queryFn: () => service.getRegistrationHistory(500), staleTime: 60_000 });
  const meetings = useQuery({ queryKey: anatomyKeys.meetings(), queryFn: () => service.getMeetings(), staleTime: 60_000 });
  const refresh = () => { queryClient.fetchQuery({ queryKey: anatomyKeys.dashboard(), queryFn: () => service.getDashboard(true) }); followUps.refetch(); registrations.refetch(); meetings.refetch(); };

  const data = snapshot.data; const status = data?.status;
  const series = useMemo(() => dailySeries(followUps.data ?? [], registrations.data ?? []), [followUps.data, registrations.data]);
  const last14 = series.slice(-14);
  const sum = (rows: typeof series, f: 'followUps' | 'registrations' | 'queued' | 'failed') => rows.reduce((n, r) => n + r[f], 0);
  const thisWeek = series.slice(-7); const prevWeek = series.slice(-14, -7);

  const outcomes = useMemo(() => {
    const all = [...(followUps.data ?? []), ...(registrations.data ?? [])].filter((i) => { const d = stampToDate(i.logDateTime); return d && d.getTime() >= Date.now() - DAYS * 86400000; });
    const count = (k: string) => all.filter((i) => resultOf(i) === k).length;
    return { recorded: count('recorded'), queued: count('queued'), failed: count('failed'), total: all.length };
  }, [followUps.data, registrations.data]);

  const byType = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of followUps.data ?? []) { const d = stampToDate(i.logDateTime); if (!d || d.getTime() < Date.now() - DAYS * 86400000) continue; const k = i.referenceName ?? (i.referenceId ? `#${i.referenceId}` : 'Unknown'); m.set(k, (m.get(k) ?? 0) + 1); }
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8).map(([type, count]) => ({ type, count }));
  }, [followUps.data]);

  const capacity = useMemo(() => (meetings.data ?? [])
    .filter((m) => m.maxRegistrations > 0)
    .sort((a, b) => (a.startDateTime ?? '').localeCompare(b.startDateTime ?? ''))
    .slice(0, 8)
    .map((m) => ({ meeting: m.topic.length > 22 ? `${m.topic.slice(0, 21)}…` : m.topic, registered: m.registeredCount, remaining: Math.max(0, m.maxRegistrations - m.registeredCount) })), [meetings.data]);

  const successRate = outcomes.total ? Math.round(((outcomes.recorded + outcomes.queued) / outcomes.total) * 100) : 100;

  if (snapshot.isLoading || !data || !status) {
    return (
      <LayoutContainer><FadeIn><Stack gap="lg">
        <Skeleton height={36} width={220} />
        <SimpleGrid cols={{ base: 2, md: 4 }}>{[1, 2, 3, 4].map((i) => <Skeleton key={i} height={120} radius="md" />)}</SimpleGrid>
        <Grid><Grid.Col span={{ base: 12, md: 8 }}><Skeleton height={320} radius="md" /></Grid.Col><Grid.Col span={{ base: 12, md: 4 }}><Skeleton height={320} radius="md" /></Grid.Col></Grid>
        {snapshot.error && <Alert color="red" title="Could not load the dashboard">{snapshot.error instanceof Error ? snapshot.error.message : 'Try again.'}</Alert>}
      </Stack></FadeIn></LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <PageHeader title="Anatomy" subtitle="Follow-ups and meeting registrations sent to the Anatomy church platform. Known people are recorded there immediately; unknown people are queued for Anatomy’s administrator."
            actions={<>
              <Button variant="light" leftSection={<IconRefresh size={16} />} loading={snapshot.isFetching && !snapshot.isLoading} onClick={refresh}>Refresh</Button>
              <Button variant="light" leftSection={<IconUserPlus size={16} />} onClick={() => navigate('/app/anatomy/meetings/register')}>Register for meeting</Button>
              <Button leftSection={<IconSend size={16} />} onClick={() => navigate('/app/anatomy/follow-ups/new')}>Send follow-up</Button>
            </>} />

          {!status.enabled && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Integration switched off">
              Nothing is sent to Anatomy until an administrator enables it in Settings{status.configured ? '' : ' and sets the API key'}.
              {canSettings && <Button variant="subtle" size="compact-sm" ml="xs" onClick={() => navigate('/app/anatomy/settings')}>Open Settings</Button>}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <KpiCardWithSparkline title="Follow-ups this week" value={sum(thisWeek, 'followUps')} description={`${data.last30Days.followUps} in 30 days`} icon={<IconSend size={20} />} color="green"
              trend={trendOf(sum(thisWeek, 'followUps'), sum(prevWeek, 'followUps'), 'vs last week')} sparklineData={last14.map((d) => d.followUps)} onClick={() => navigate('/app/anatomy/follow-ups/sent')} />
            <KpiCardWithSparkline title="Registrations this week" value={sum(thisWeek, 'registrations')} description={`${data.upcomingMeetings} upcoming meeting${data.upcomingMeetings === 1 ? '' : 's'}${data.meetingsFull ? `, ${data.meetingsFull} full` : ''}`} icon={<IconCalendarEvent size={20} />} color="blue"
              trend={trendOf(sum(thisWeek, 'registrations'), sum(prevWeek, 'registrations'), 'vs last week')} sparklineData={last14.map((d) => d.registrations)} onClick={() => navigate('/app/anatomy/meetings/registrations')} />
            <KpiCardWithSparkline title="Queued for Anatomy" value={sum(thisWeek, 'queued')} description="Unknown people waiting for Anatomy’s admin" icon={<IconAlertTriangle size={20} />} color="yellow"
              trend={trendOf(sum(thisWeek, 'queued'), sum(prevWeek, 'queued'), 'vs last week')} sparklineData={last14.map((d) => d.queued)} onClick={() => navigate('/app/anatomy/activity')} />
            <KpiCardWithSparkline title="Linked members" value={data.linkedMembers} description={data.last30Days.checksServedFromLink ? `${data.last30Days.checksServedFromLink} checks answered from CCMS in 30 days` : 'Members carrying an Anatomy id'} icon={<IconLink size={20} />} color="violet"
              sparklineData={last14.map((d) => d.followUps + d.registrations)} onClick={() => navigate('/app/anatomy/test-user')} />
          </SimpleGrid>

          <Grid align="stretch">
            <Grid.Col span={{ base: 12, lg: 8 }}>
              <AreaChartWidget title="Sent to Anatomy — last 30 days" subtitle="Recorded follow-ups and registrations per day" data={series} xAxisKey="day" height={CHART_H} showGrid showLegend
                areas={[{ dataKey: 'followUps', name: 'Follow-ups', color: 'var(--mantine-color-green-6)', fillOpacity: 0.25 }, { dataKey: 'registrations', name: 'Registrations', color: 'var(--mantine-color-blue-6)', fillOpacity: 0.25 }]} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 4 }}>
              <DonutChartWidget title="Outcomes — 30 days" subtitle="What Anatomy did with each request" height={CHART_H} showLegend showPercentages centerLabel={`${outcomes.total}`}
                data={[{ name: 'Recorded', value: outcomes.recorded, color: 'var(--mantine-color-green-6)' }, { name: 'Queued', value: outcomes.queued, color: 'var(--mantine-color-yellow-6)' }, { name: 'Failed', value: outcomes.failed, color: 'var(--mantine-color-red-6)' }]} />
            </Grid.Col>
          </Grid>

          <Grid align="stretch">
            <Grid.Col span={{ base: 12, lg: 4 }}>
              {byType.length ? (
                <BarChartWidget title="Follow-ups by type — 30 days" subtitle="Anatomy’s follow-up types, most used first" data={byType} xAxisKey="type" orientation="horizontal" height={CHART_H} showGrid
                  bars={[{ dataKey: 'count', name: 'Follow-ups', color: 'var(--mantine-color-green-6)' }]} />
              ) : (
                <ChartPlaceholder title="Follow-ups by type — 30 days" subtitle="Anatomy’s follow-up types, most used first" text="No follow-ups sent in the last 30 days." />
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 4 }}>
              {capacity.length ? (
                <BarChartWidget title="Meeting capacity" subtitle="Next meetings with a limit — registered vs remaining" data={capacity} xAxisKey="meeting" stacked height={CHART_H} showLegend
                  bars={[{ dataKey: 'registered', name: 'Registered', color: 'var(--mantine-color-blue-6)', stackId: 'c' }, { dataKey: 'remaining', name: 'Remaining', color: 'var(--mantine-color-gray-5)', stackId: 'c' }]} />
              ) : (
                <ChartPlaceholder title="Meeting capacity" subtitle="Next meetings with a limit — registered vs remaining" text={meetings.data?.length ? `${meetings.data.length} upcoming meeting${meetings.data.length === 1 ? '' : 's'}, none with a registration limit.` : 'No upcoming meetings.'} />
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 4 }}>
              <CircularGauge title="Accepted by Anatomy" value={successRate} max={100} unit="%" description={`${outcomes.total} request${outcomes.total === 1 ? '' : 's'} in 30 days · ${outcomes.failed} failed`} showPercentage size="lg" color={successRate >= 95 ? 'green' : successRate >= 80 ? 'yellow' : 'red'} />
            </Grid.Col>
          </Grid>

          <Card withBorder radius="md" p="md">
            <Group justify="space-between" wrap="wrap" gap="md">
              <Group gap="md" wrap="wrap">
                <Group gap={6}><IconPlugConnected size={16} /><Text fw={600}>Connection</Text></Group>
                <Badge color={status.enabled ? 'green' : 'gray'} variant="light">{status.enabled ? 'Enabled' : 'Off'}</Badge>
                <Badge color={status.configured ? 'green' : 'red'} variant="light">{status.configured ? 'Configured' : 'No API key'}</Badge>
                <Badge color="blue" variant="light">{data.followUpTypes} follow-up types</Badge>
                <Text size="sm" c="dimmed">Last test {formatRelative(status.lastTestDateTime)}{status.lastTestResult ? ` — ${status.lastTestResult}` : ''}</Text>
                <Text size="sm" c="dimmed">{status.callsLast24h} calls · {status.failuresLast24h} failed · {status.queuedLast24h} queued in 24 h</Text>
              </Group>
              <Group gap={4} wrap="wrap">
                {status.stagingUrl && <Button component="a" href={status.stagingUrl} target="_blank" rel="noreferrer" variant="subtle" size="compact-sm" rightSection={<IconExternalLink size={14} />}>Anatomy staging</Button>}
                <Button variant="subtle" size="compact-sm" leftSection={<IconTestPipe size={14} />} onClick={() => navigate('/app/anatomy/test-user')}>Test user</Button>
                <Button variant="subtle" size="compact-sm" leftSection={<IconListDetails size={14} />} onClick={() => navigate('/app/anatomy/activity')}>Activity</Button>
                {canSettings && <Button variant="subtle" size="compact-sm" leftSection={<IconSettings size={14} />} onClick={() => navigate('/app/anatomy/settings')}>Settings</Button>}
              </Group>
            </Group>
          </Card>

          <Grid align="stretch">
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Card withBorder radius="md" p={0} h="100%">
                <Group justify="space-between" p="md" pb="xs"><Text fw={600}>Latest follow-ups</Text><Button variant="subtle" size="compact-sm" onClick={() => navigate('/app/anatomy/follow-ups/sent')}>All sent</Button></Group>
                <ActivityTable items={data.recentFollowUps} kind="follow-up" compact emptyTitle="No follow-ups yet" emptyDescription="Send one from Follow-ups." />
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, lg: 6 }}>
              <Card withBorder radius="md" p={0} h="100%">
                <Group justify="space-between" p="md" pb="xs"><Text fw={600}>Latest registrations</Text><Button variant="subtle" size="compact-sm" onClick={() => navigate('/app/anatomy/meetings/registrations')}>All registrations</Button></Group>
                <ActivityTable items={data.recentRegistrations} kind="registration" compact emptyTitle="No registrations yet" emptyDescription="Register someone for an upcoming meeting." />
              </Card>
            </Grid.Col>
          </Grid>
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
